'use client';

import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';

import { FlexCol, FlexRow } from '@/components/ui/flex';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DataTableEmptyRow } from '@/pages/Documents/FilesTable/data-table-empty-row';
import { DataTablePagination } from '@/pages/Documents/FilesTable/data-table-pagination';
import { DataTableViewOptions } from '@/pages/Documents/FilesTable/data-table-view-options';
import React, { useEffect } from 'react';
import { FileTableActionBar } from './FileTableActionBar';

interface SharedKnowledgeDataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  onClearFilters?: () => void;
  onUploadFiles?: () => void;
  // External pagination props
  externalPagination?: {
    page: number;
    pageSize: number;
    totalPages: number;
    total: number;
    hasNext: boolean;
    hasPrev: boolean;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
}

export function SharedKnowledgeDataTable<TData, TValue>({
  columns,
  data,
  onClearFilters,
  onUploadFiles,
  externalPagination,
}: SharedKnowledgeDataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [rowSelection, setRowSelection] = React.useState({});
  const [open, setOpen] = React.useState(false);

  const clearRowSelection = React.useCallback(() => {
    setRowSelection({});
  }, []);

  useEffect(() => {
    setOpen(Object.keys(rowSelection).length > 0);
  }, [rowSelection]);

  const table = useReactTable({
    data,
    columns,
    getRowId: (row: any) => row.id,
    getCoreRowModel: getCoreRowModel(),
    // Only use built-in pagination if no external pagination is provided
    ...(externalPagination
      ? {}
      : { getPaginationRowModel: getPaginationRowModel() }),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    enableMultiSort: false,
    // Use external pagination state if provided
    ...(externalPagination && {
      manualPagination: true,
      pageCount: externalPagination.totalPages,
      state: {
        sorting,
        columnFilters,
        rowSelection,
        pagination: {
          pageIndex: externalPagination.page - 1, // TanStack Table uses 0-based indexing
          pageSize: externalPagination.pageSize,
        },
      },
      onPaginationChange: updater => {
        if (typeof updater === 'function') {
          const newState = updater({
            pageIndex: externalPagination.page - 1,
            pageSize: externalPagination.pageSize,
          });
          externalPagination.onPageChange(newState.pageIndex + 1);
          externalPagination.onPageSizeChange(newState.pageSize);
        }
      },
    }),
    state: externalPagination
      ? {
          sorting,
          columnFilters,
          rowSelection,
          pagination: {
            pageIndex: externalPagination.page - 1,
            pageSize: externalPagination.pageSize,
          },
        }
      : {
          sorting,
          columnFilters,
          rowSelection,
        },
  });

  const selectedRows: { id: string }[] = table
    .getFilteredSelectedRowModel()
    .rows.map(row => row.original);

  return (
    <FlexCol gap="6" pb="4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <DataTableEmptyRow
                columnLength={columns.length}
                onClearFilters={onClearFilters}
                onUploadFiles={onUploadFiles}
              />
            )}
          </TableBody>
        </Table>
      </div>
      <FlexRow alignItems="center" w="full" justifyContent="stretch">
        <DataTableViewOptions table={table} />
        <DataTablePagination
          table={table}
          externalPagination={externalPagination}
        />
      </FlexRow>

      <FileTableActionBar
        open={open}
        setOpen={setOpen}
        selectedRows={selectedRows}
        onClearSelection={clearRowSelection}
      />
    </FlexCol>
  );
}
